/**
 * Shared helpers for lookup field filter configuration and runtime resolution.
 */

/** True when logic is the simple combiners saved as literal AND / OR. */
export function isSimpleFilterLogic(logic) {
  const u = String(logic ?? '').trim().toUpperCase();
  return u === 'AND' || u === 'OR';
}

export function defaultCustomFilterLogicExpression(validFilterCount) {
  if (validFilterCount <= 0) return '1';
  if (validFilterCount === 1) return '1';
  return Array.from({ length: validFilterCount }, (_, i) => String(i + 1)).join(' AND ');
}

/** Normalize logic for API/query params (AND/OR uppercase; custom expression preserved). */
export function resolveEffectiveLookupFilterLogic(logic) {
  const trimmed = String(logic ?? '').trim();
  if (!trimmed) return 'AND';
  if (isSimpleFilterLogic(trimmed)) return trimmed.toUpperCase();
  return trimmed;
}

/** Normalize logic when persisting simple AND/OR; keep custom expressions as entered. */
export function normalizeStoredLookupFilterLogic(logic) {
  const trimmed = String(logic ?? '').trim();
  if (!trimmed) return 'AND';
  if (isSimpleFilterLogic(trimmed)) return trimmed.toUpperCase();
  return trimmed;
}

/**
 * Parse lookup_filter from field config into filters array + logic string.
 * Supports legacy array format and { logic, filters } object (logic may be custom).
 */
export function extractLookupFilter(field) {
  let lookupFilterArray = null;
  let lookupFilterLogic = 'AND';

  const lookupFilter = field?.lookupFilter ?? field?.lookup_filter;
  if (!lookupFilter) {
    return { lookupFilterArray, lookupFilterLogic };
  }

  const applyLogic = (rawLogic, fallback = 'AND') => {
    if (rawLogic == null || String(rawLogic).trim() === '') return fallback;
    return normalizeStoredLookupFilterLogic(rawLogic);
  };

  if (typeof lookupFilter === 'string') {
    try {
      const parsed = JSON.parse(lookupFilter);
      if (typeof parsed === 'object' && !Array.isArray(parsed) && parsed.filters) {
        lookupFilterArray = Array.isArray(parsed.filters) ? parsed.filters : null;
        lookupFilterLogic = applyLogic(parsed.logic);
      } else if (Array.isArray(parsed)) {
        lookupFilterArray = parsed;
        lookupFilterLogic = applyLogic(
          field?.lookup_filter_logic ?? field?.lookupFilterLogic,
          'AND'
        );
      }
    } catch (e) {
      console.error('Failed to parse lookup_filter:', e);
    }
  } else if (
    typeof lookupFilter === 'object' &&
    !Array.isArray(lookupFilter) &&
    lookupFilter.filters
  ) {
    lookupFilterArray = Array.isArray(lookupFilter.filters) ? lookupFilter.filters : null;
    lookupFilterLogic = applyLogic(lookupFilter.logic);
  } else if (Array.isArray(lookupFilter)) {
    lookupFilterArray = lookupFilter;
    lookupFilterLogic = applyLogic(
      field?.lookup_filter_logic ?? field?.lookupFilterLogic,
      'AND'
    );
  }

  return { lookupFilterArray, lookupFilterLogic };
}
